import {Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgbActiveModal, NgbModal, NgbTooltip} from '@ng-bootstrap/ng-bootstrap';
import {CampaignsApiService} from '@core/services/api/campaigns-api.service';
import {AlertService} from '@core/services/alert.service';
import {AppRichTextComponent} from '@shared/components/forms/app-rich-text/app-rich-text.component';
import {CompanyDetailsComponent} from '@features/companies/components/company-details/company-details.component';
import {CpfCnpjPipe} from "@shared/pipes/app-cpfcnpj.pipe";
import {AppPhoneComponent} from "@shared/components/commons/app-phone/app-phone.component";
import {ConfirmationModalComponent} from "@shared/components/commons/confirmation-modal/confirmation-modal.component";
import {NegotiationStrategyModalComponent} from "../negotiation-strategy-modal/negotiation-strategy-modal.component";
import {CompanyDebtsComponent} from "@features/companies/components/company-debts/company-debts.component";

@Component({
    selector: 'app-task-details-modal',
    standalone: true,
    imports: [CommonModule, FormsModule, AppRichTextComponent, CpfCnpjPipe, NgbTooltip, AppPhoneComponent],
    templateUrl: './task-details-modal.component.html',
    styleUrl: './task-details-modal.component.scss'
})
export class TaskDetailsModalComponent implements OnInit {
    activeModal = inject(NgbActiveModal);
    private campaignsApiService = inject(CampaignsApiService);
    private alertService = inject(AlertService);
    private modalService = inject(NgbModal);

    // Inputs from parent
    campaignId!: number;
    taskId!: number;
    allowRemoveFromCampaign: boolean = true; // Default to true for backward compatibility
    allowAssigneeManagement: boolean = true; // Default to true for backward compatibility

    // State
    protected isLoading = signal(true);
    protected task = signal<any>(null);
    protected comments = signal<any[]>([]);
    protected timeline = signal<any[]>([]);
    protected users = signal<any[]>([]);

    // UI state
    protected activeTab = signal<'comments' | 'timeline'>('comments');
    protected newComment = '';
    protected isSavingComment = signal(false);
    protected openAssigneeDropdown = signal(false);

    ngOnInit(): void {
        this.loadTaskDetails();
        this.loadCampaignUsers();
    }

    private loadTaskDetails(): void {
        this.isLoading.set(true);

        this.campaignsApiService.getCampaignCompanyDetails(this.campaignId, this.taskId).subscribe({
            next: (response: any) => {
                if (response.success && response.data) {
                    this.task.set(response.data.task);
                    this.comments.set(response.data.comments || []);
                    this.timeline.set(response.data.timeline || []);
                }
                this.isLoading.set(false);
            },
            error: () => {
                this.alertService.operationFailure();
                this.isLoading.set(false);
            }
        });
    }

    private loadCampaignUsers(): void {
        this.campaignsApiService.getCampaignUsers().subscribe({
            next: (response: any) => {
                if (response.success && response.data) {
                    this.users.set(response.data);
                }
            },
            error: () => {
                console.error('Failed to load users');
            }
        });
    }

    protected addComment(): void {
        if (!this.newComment.trim()) return;

        this.isSavingComment.set(true);

        this.campaignsApiService.addTaskComment(this.campaignId, this.taskId, this.newComment).subscribe({
            next: (response: any) => {
                if (response.success) {
                    this.newComment = '';
                    this.loadTaskDetails(); // Reload to get new comment
                    this.alertService.success('Sucesso', 'Comentário adicionado');
                }
                this.isSavingComment.set(false);
            },
            error: () => {
                this.alertService.error('Erro', 'Falha ao adicionar comentário');
                this.isSavingComment.set(false);
            }
        });
    }

    protected assignUser(user: any): void {
        this.openAssigneeDropdown.set(false);

        this.campaignsApiService.updateTaskAssignee(this.campaignId, this.taskId, user.id).subscribe({
            next: (response: any) => {
                if (response.success) {
                    this.loadTaskDetails(); // Reload to update assignee
                    this.alertService.success('Sucesso', 'Vendedor atribuído');
                }
            },
            error: () => {
                this.alertService.error('Erro', 'Falha ao atribuir vendedor');
            }
        });
    }

    protected unassignUser(): void {
        this.openAssigneeDropdown.set(false);

        this.campaignsApiService.updateTaskAssignee(this.campaignId, this.taskId, null).subscribe({
            next: (response: any) => {
                if (response.success) {
                    this.loadTaskDetails();
                    this.alertService.success('Sucesso', 'Vendedor removido');
                }
            },
            error: () => {
                this.alertService.error('Erro', 'Falha ao remover vendedor');
            }
        });
    }

    protected getStatusLabel(status: string): string {
        const labels: Record<string, string> = {
            'PENDING': 'Pendente',
            'IN_CONTACT': 'Em Contato',
            'PROPOSAL_SENT': 'Proposta Enviada',
            'UNDER_NEGOTIATION': 'Em Negociação',
            'CLOSED': 'Fechado',
            'LOST': 'Perdido'
        };
        return labels[status] || status;
    }

    protected getStatusColor(status: string): string {
        const colors: Record<string, string> = {
            'PENDING': 'primary',
            'IN_CONTACT': 'info',
            'PROPOSAL_SENT': 'warning',
            'UNDER_NEGOTIATION': 'secondary',
            'CLOSED': 'success',
            'LOST': 'danger'
        };
        return colors[status] || 'secondary';
    }

    protected getUserInitials(user: any): string {
        if (!user) return '';
        return `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase();
    }

    protected hasMetadata(metadata: any): boolean {
        if (!metadata) return false;
        if (typeof metadata === 'object' && Object.keys(metadata).length === 0) return false;
        return true;
    }

    protected viewCompanyDetails(): void {
        const companyId = this.task()?.companyId;
        if (!companyId) return;

        const modalRef = this.modalService.open(CompanyDetailsComponent, {
            size: 'xl',
            centered: true,
            scrollable: true
        });

        modalRef.componentInstance.companyId = companyId;
    }

    viewDebts(): void {
        const companyId = this.task()?.companyId;
        if (companyId) {
            const modalRef = this.modalService.open(CompanyDebtsComponent, {
                size: 'fullscreen',
                centered: true,
                scrollable: true
            });

            modalRef.componentInstance.companyId = companyId;
        }
    }

    protected openNegotiationStrategy(): void {
        const companyId = this.task()?.companyId;
        if (!companyId) return;

        const modalRef = this.modalService.open(NegotiationStrategyModalComponent, {
            size: 'xl',
            centered: true,
            scrollable: true
        });

        modalRef.componentInstance.companyId = companyId;
    }

    protected removeCompanyFromCampaign(): void {
        const companyName = this.task()?.company?.name || 'esta empresa';

        const modalRef = this.modalService.open(ConfirmationModalComponent, {
            centered: true
        });

        modalRef.componentInstance.config = {
            title: 'Remover Empresa da Campanha',
            message: `Tem certeza que deseja remover "${companyName}" desta campanha?\n\nEsta ação não pode ser desfeita e todos os dados relacionados a esta task serão perdidos.`,
            confirmText: 'Sim, Remover',
            cancelText: 'Cancelar',
            confirmButtonClass: 'btn-danger',
            icon: 'ti-alert-triangle',
            iconClass: 'text-danger'
        };

        modalRef.result.then(
            (confirmed) => {
                if (!confirmed) return;

                this.isLoading.set(true);

                this.campaignsApiService.removeCompaniesFromCampaign(this.campaignId, [this.taskId]).subscribe({
                    next: (response: any) => {
                        if (response.success) {
                            this.alertService.success(
                                'Empresa Removida',
                                `${companyName} foi removida da campanha com sucesso.`
                            );
                            // Close modal and notify parent to reload
                            this.activeModal.close('deleted');
                        }
                    },
                    error: (error) => {
                        console.error('Error removing company from campaign:', error);
                        this.alertService.error(
                            'Erro ao Remover',
                            error.error?.message || 'Não foi possível remover a empresa da campanha.'
                        );
                        this.isLoading.set(false);
                    }
                });
            },
            () => {
                // Modal dismissed (user cancelled)
            }
        );
    }

    close(): void {
        this.activeModal.close();
    }
}
